import { SyntheticEvent } from 'react';
import { Box, Tab, Tabs, Theme, Tooltip, ToggleButton } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MultipleSelectionIcon from 'components/icons/MultipleSelectionIcons/MultipleSelectionIcon';
import { getStyleVariables } from 'styles/vars';

export type SessionListTab = 'my' | 'shared';

const useStyles = makeStyles()((theme: Theme) => {
  const styleVars = getStyleVariables(theme);
  return {
    container: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      margin: `var(--gds-spacing-sm) var(--gds-spacing-xl) 0 var(--gds-spacing-xl)`,
      width: '332px',
    },
    tabs: {
      minHeight: '36px',
    },
    tab: {
      minHeight: '36px',
      padding: theme.spacing(0.5, 1),
      marginRight: theme.spacing(2),
      textTransform: 'none',
      fontFamily: '"Open Sans", "Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '16px',
      letterSpacing: 0,
      minWidth: 'auto',
    },
    actions: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
    },
    iconButton: {
      borderRadius: '50%',
      width: '24px',
      height: '24px',
      padding: 0,
      margin: 0,
      color: 'rgba(255,255,255,.7)',
      border: 'none',
      '&:hover': {
        color: 'rgba(255,255,255)',
        backgroundColor: 'rgba(255,255,255,.04)',
      },
      '&.Mui-selected': {
        backgroundColor: styleVars.colors.red,
      },
      '&.Mui-selected:hover': {
        backgroundColor: styleVars.colors.red,
      },
    },
    icon: {
      width: theme.spacing(2),
      height: theme.spacing(2),
      cursor: 'pointer',
    },
  };
});

export const MIN_INSPECTIONS_FOR_MULTI_REPORT = 2;

interface SessionListTabsProps {
  activeTab: SessionListTab;
  onTabChange: (tab: SessionListTab) => void;
  isSearchActive?: boolean;
  inspectionCount: number;
  /** Loaded inspections on the inactive tab. Ignored while search is active (search already spans both). */
  otherTabInspectionCount?: number;
  multiSelectionMode: boolean;
  onToggleMultiSelectionMode: () => void;
  selectedInspectionCount: number;
  onGenerateMultiReport: () => void;
}

const SessionListTabs = ({
  activeTab,
  onTabChange,
  isSearchActive = false,
  inspectionCount,
  otherTabInspectionCount = 0,
  multiSelectionMode,
  onToggleMultiSelectionMode,
  selectedInspectionCount,
  onGenerateMultiReport,
}: SessionListTabsProps) => {
  const { classes } = useStyles();
  // Selections persist across tabs, so the entry point must count both inventories.
  // Search results already include owned + shared, so don't add the other tab then.
  const inventoryCount = isSearchActive ? inspectionCount : inspectionCount + otherTabInspectionCount;
  const showMultiReportToggle = inventoryCount >= MIN_INSPECTIONS_FOR_MULTI_REPORT || multiSelectionMode;

  const handleTabChange = (_event: SyntheticEvent, newValue: SessionListTab) => {
    onTabChange(newValue);
  };

  return (
    <Box className={classes.container}>
      <Tabs
        // While a search is in progress the panel leaves the tab context, so neither tab is highlighted.
        value={isSearchActive ? false : activeTab}
        onChange={handleTabChange}
        textColor="inherit"
        indicatorColor="secondary"
        scrollButtons={false}
        className={classes.tabs}
      >
        <Tab value="my" label="My Inspections" className={classes.tab} data-testid="my-inspections-tab" />
        <Tab value="shared" label="Shared" className={classes.tab} data-testid="shared-inspections-tab" />
      </Tabs>
      <Box className={classes.actions}>
        {showMultiReportToggle && (
          <Tooltip title="Generate multi-inspection report" placement="top">
            <ToggleButton
              value="multiReportButton"
              selected={multiSelectionMode}
              className={classes.iconButton}
              onClick={onToggleMultiSelectionMode}
              disableRipple
              aria-label="Toggle multi-report selection"
            >
              <MultipleSelectionIcon className={classes.icon} />
            </ToggleButton>
          </Tooltip>
        )}
        {selectedInspectionCount > 0 && (
          <Tooltip title="Generate report for selected inspections (including all linked inspections)" placement="top">
            <ToggleButton
              value="generateMultiReport"
              selected={false}
              className={classes.iconButton}
              onClick={onGenerateMultiReport}
              disableRipple
              aria-label="Generate multi-report"
              data-testid="GenerateMultiReportButton"
            >
              <AssessmentIcon className={classes.icon} />
            </ToggleButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default SessionListTabs;
